import { Card, DatePicker, Form, notification, Popconfirm, Space } from 'antd';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useQuery } from 'react-query';
import { PlusOutlined } from '@ant-design/icons';
import { useForm } from 'antd/lib/form/Form';
import moment from 'moment';
import { isNil } from 'lodash';
import {
  addMarketValue,
  deleteMarketValue,
  FinAssetMarketValue,
  getMarketValueListByAssetID,
  updateMarketValue,
} from '../../../services/finassets';
import DataGrid, { DataGridColDef } from 'components/DataGrid/DataGrid';
import Button from 'components/Button';
import { useFormList } from 'hooks/useFormList';
import {
  toDatePickerDate,
  dateToString,
  generateId,
  isGeneratedId,
  shouldUpdateCompare,
} from 'utils/utils2';
import GetLabel from 'DisplayFramework/components/GetLabel';
import DeleteButton from 'components/DeleteButton';
import ParListSelect from 'components/ParListSelect';
import { formatDate } from 'components/DataGrid';
import SaveButtonIcon from 'components/SaveBtn';
import InputCurrency from 'components/InputCurrency';

type AssetMarketValueProps = {
  assetId?: number;
  showAsModal?: boolean;
  currcode?: string;
  status?: string;
  fromproposal?: boolean;
  refetchMarketValues?: boolean;
};

const AssetMarketValue = (props: AssetMarketValueProps) => {
  const {
    assetId,
    showAsModal,
    currcode,
    status,
    fromproposal,
    refetchMarketValues,
  } = props;

  const [form] = useForm();

  const { add, remove } = useFormList(form, 'merketValues', { idKey: 'mvid' });

  const { data: merketValues, refetch } = useGetMarketValues(assetId);

  useEffect(() => {
    form.setFieldsValue({
      merketValues: merketValues?.map(marketValueToFormValue) ?? [],
      currcode: currcode,
    });
    refetch();
  }, [form, merketValues, currcode, refetchMarketValues]);

  const handleAdd = () => {
    add({
      assetid: assetId,
      created_at: moment(),
      created_by: '',
      mvid: generateId(),
      mvsource: '',
      startdate: '',
      valueamount: 0,
    } as any);
  };

  const update = useCallback(
    (index: number, updatedMarketValue: FinAssetMarketValue) => {
      const formMarketValues = form.getFieldValue(
        'merketValues'
      ) as FinAssetMarketValue[];

      form.setFieldsValue({
        merketValues: formMarketValues.map((mv, i) =>
          i === index ? marketValueToFormValue(updatedMarketValue) : mv
        ),
      });
    },
    [form]
  );

  const handleSave = useCallback(
    (index: number) => {
      if (assetId === undefined) {
        return;
      }

      const mv = form.getFieldValue(['merketValues', index]);

      const marketValue = {
        ...mv,
        mvid: isGeneratedId(mv.mvid) ? null : mv.mvid,
        created_at: mv.created_at,
        startdate: dateToString(mv.startdate),
      };

      if (isGeneratedId(mv.mvid)) {
        addMarketValue({
          assetid: assetId,
          marketValue,
        }).then((res) => {
          update(index, res.data);
          refetch();
          notification.success({
            description:res.description,
            message:res.message,
          });
        });
      } else {
        updateMarketValue({
          assetid: assetId,
          mvid: marketValue.mvid,
          marketValue,
        }).then((res) => {
          update(index, res.data);
          refetch();
          notification.success({
            description:res.description,
            message:res.message,
          });
        });
      }
    },
    [form, assetId, update]
  );

  const handledelete = async (assetid: number, mvid: number) => {
    await deleteMarketValue({ assetid: assetid, mvid: mvid })
      .then((res) => {
        notification.success({ message: res.message });
      })
      .catch((res) => {
        notification.error({ message: res.message });
      });
    remove(mvid);
  };

  const columns = useMemo<DataGridColDef<FinAssetMarketValue>[]>(
    () => [
      {
        comcode: 'marketvalue.mvsource',
        key: 'mvsource',
        width: 100,
        render: (_value, _record, index) => (
          <Form.Item name={['merketValues', index, 'mvsource']} noStyle>
            <ParListSelect parcode="MVSOURCE" />
          </Form.Item>
        ),
      },
      {
        comcode: 'marketvalue.valueamount',
        key: 'valueamount',
        width: 100,
        align: 'left',
        render: (_value, _record, index) => (
          <Form.Item name={['merketValues', index, 'valueamount']} noStyle>
            <InputCurrency currcode={form.getFieldValue('currcode')} />
          </Form.Item>
        ),
      },
      {
        comcode: 'marketvalue.startdate',
        dataIndex: 'startdate',
        width: 120,
        align: 'left',
        render: (_value, _record, index) => (
          <Form.Item name={['merketValues', index, 'startdate']} noStyle>
            <DatePicker />
          </Form.Item>
        ),
      },
      {
        comcode: 'marketvalue.created_at',
        dataIndex: 'created_at',
        width: 120,
        align: 'left',
        valueFormatter: formatDate,
      },
      {
        comcode: 'actions',
        key: 'actions',
        render: (_text, _record, index) => (
          <Space align="center">
            
            <SaveButtonIcon
              onClick={() => handleSave(index)}
              disabled={_record.flagupdatefv}
            >
              <GetLabel label={ isGeneratedId(_record.mvid)?"save":"update"} />
            </SaveButtonIcon>
            <Popconfirm
              title="Are you sure to delete?"
              onConfirm={() => {
                handledelete(_record.assetid, _record.mvid);
              }}
              okText="Yes"
              cancelText="No"
            >
              <DeleteButton disabled={_record.flagupdatefv} onlyIcon>
                <GetLabel label="delete" />
              </DeleteButton>
            </Popconfirm>
          </Space>
        ),
        fixed: 'right',
        width: 150,
      },
    ],
    [form, handleSave]
  );

  return (
    <Card title={<GetLabel label="assetMerketValues" style={{fontWeight:'bold',paddingLeft:15}}/>}>
      <Form component={false} form={form}>
        <Form.Item noStyle shouldUpdate={shouldUpdateCompare('merketValues')}>
          {({ getFieldValue }) => (
            <DataGrid
              columns={
                !showAsModal
                  ? columns
                  : columns.filter((col) => col.key !== 'actions')
              }
              hideFilters
              extraActions={
                !showAsModal && (
                  <Button
                    comcode="add"
                    icon={<PlusOutlined />}
                    type="primary"
                    onClick={handleAdd}
                    disabled={
                      (status === 'PROPOSAL' && fromproposal === false) ||
                      status === 'ORDER' ||
                      status === 'DELIVER'
                    }
                  />
                )
              }
              dataSource={getFieldValue('merketValues')}
              rowKey={(item) => item.mvid}
            />
          )}
        </Form.Item>
      </Form>
    </Card>
  );
};

const useGetMarketValues = (assetId?: number) => {
  return useQuery(
    ['getMarketValueListByAssetID', assetId],
    () =>
      getMarketValueListByAssetID({ assetid: assetId! }).then(
        (res) => res.list || []
      ),
    {
      enabled: !isNil(assetId),
    }
  );
};

const marketValueToFormValue = (mv: FinAssetMarketValue) => ({
  assetid: mv.assetid,
  created_at: toDatePickerDate(mv.created_at),
  created_by: mv.created_by,
  mvid: mv.mvid,
  mvsource: mv.mvsource,
  startdate: toDatePickerDate(mv.startdate),
  valueamount: mv.valueamount,
  flagupdatefv: mv.flagupdatefv,
});

export default AssetMarketValue;
